import type { ReactNode } from "react";
import {
  Avatar,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Link,
  List,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type { AutoTransactionRuleDto } from "../../../../api/types";
import { CopyableText } from "../../../../components/CopyableText";
import { useSortableAccessibility } from "../../../../components/SortableList";
import { AdminListRow } from "../../../../components/AdminList";
import { AutoTransactionActions } from "./AutoTransactionActions";
import { SortableRow } from "./SortableRow";
import { StackedRuleBody } from "./StackedRuleBody";

export type Selection = {
  selectedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
};

export type DragHandlers = {
  /** Called with the rule ids in their new order after a successful drop. */
  onReorder: (ids: string[]) => void;
};

export type AutoTransactionListViewProps = {
  rules: AutoTransactionRuleDto[];
  togglingId: string | null;
  /** When set, each row shows a checkbox instead of the priority avatar. */
  selection?: Selection | null;
  /** When set, rows become drag-sortable with a handle in place of the avatar. */
  dragHandlers?: DragHandlers | null;
  /** Per-rule expand state, lifted here so toggles survive parent re-renders. */
  expandedIds: ReadonlySet<string>;
  onToggleExpanded: (id: string) => void;
  onEdit: (rule: AutoTransactionRuleDto) => void;
  onDuplicate: (rule: AutoTransactionRuleDto) => void;
  onPreviewMatches: (rule: AutoTransactionRuleDto) => void;
  onShowApplied: (rule: AutoTransactionRuleDto) => void;
  onDelete: (rule: AutoTransactionRuleDto) => void;
  onToggleEnabled: (rule: AutoTransactionRuleDto, next: boolean) => void;
};

type RowProps = {
  rule: AutoTransactionRuleDto;
  divider: boolean;
  togglingId: string | null;
  /** Renders into the avatar slot — checkbox, drag handle, or priority avatar. */
  leftSlot: ReactNode;
  /** When true, the priority chip is shown next to the name so it stays visible. */
  showPriorityChip: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  onEdit: (rule: AutoTransactionRuleDto) => void;
  onDuplicate: (rule: AutoTransactionRuleDto) => void;
  onPreviewMatches: (rule: AutoTransactionRuleDto) => void;
  onShowApplied: (rule: AutoTransactionRuleDto) => void;
  onDelete: (rule: AutoTransactionRuleDto) => void;
  onToggleEnabled: (rule: AutoTransactionRuleDto, next: boolean) => void;
};

const Row = ({
  rule,
  divider,
  togglingId,
  leftSlot,
  showPriorityChip,
  expanded,
  onToggleExpanded,
  onEdit,
  onDuplicate,
  onPreviewMatches,
  onShowApplied,
  onDelete,
  onToggleEnabled,
}: RowProps) => (
  <AdminListRow
    divider={divider}
    avatar={leftSlot}
    primary={
      <Stack
        component="span"
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap" }}
      >
        <IconButton
          size="small"
          aria-label={expanded ? "collapse details" : "expand details"}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpanded();
          }}
          sx={{
            p: 0.25,
            color: "text.secondary",
            transition: "transform 150ms",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
        {rule.editable ? (
          <Link
            component="button"
            type="button"
            variant="body1"
            onClick={() => onEdit(rule)}
            aria-label={`Edit rule ${rule.name}`}
            underline="hover"
            sx={{
              textAlign: "left",
              color: rule.enabled ? "text.primary" : "text.disabled",
              textDecoration: rule.enabled ? "none" : "line-through",
              fontWeight: 500,
              minHeight: 32,
            }}
          >
            {rule.name}
          </Link>
        ) : (
          // Read-only virtual rule: the name is not an edit link.
          <Typography
            variant="body1"
            sx={{
              display: "flex",
              alignItems: "center",
              textAlign: "left",
              color: "text.primary",
              fontWeight: 500,
              minHeight: 32,
            }}
          >
            {rule.name}
          </Typography>
        )}
        {showPriorityChip && (
          <Chip
            size="small"
            label={`P${rule.priority}`}
            variant="outlined"
            sx={{ fontVariantNumeric: "tabular-nums" }}
          />
        )}
      </Stack>
    }
    secondary={
      <Collapse in={expanded} unmountOnExit>
        <Stack component="span" spacing={0.5} sx={{ pt: 1 }}>
          <StackedRuleBody rule={rule} />
          {rule.description && (
            <Typography
              component="span"
              variant="body2"
              sx={{ fontStyle: "italic", color: "text.secondary" }}
            >
              {rule.description}
            </Typography>
          )}
          <Typography
            component="span"
            variant="body2"
            sx={{ color: "text.secondary" }}
          >
            <CopyableText text={rule.id} maxLength={10} />
          </Typography>
        </Stack>
      </Collapse>
    }
    actions={
      <AutoTransactionActions
        rule={rule}
        isTogglingEnabled={togglingId === rule.id}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onPreviewMatches={onPreviewMatches}
        onShowApplied={onShowApplied}
        onDelete={onDelete}
        onToggleEnabled={onToggleEnabled}
      />
    }
  />
);

const priorityAvatar = (rule: AutoTransactionRuleDto) => (
  <Tooltip title={`Priority ${rule.priority}`}>
    <Avatar
      variant="rounded"
      sx={{
        bgcolor: rule.enabled ? "primary.main" : "grey.500",
        color: "primary.contrastText",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {rule.priority}
    </Avatar>
  </Tooltip>
);

const selectionCheckbox = (
  rule: AutoTransactionRuleDto,
  selection: Selection,
) => (
  <Checkbox
    checked={selection.selectedIds.has(rule.id)}
    onChange={() => selection.onToggle(rule.id)}
    slotProps={{ input: { "aria-label": `select ${rule.name}` } }}
  />
);

export const AutoTransactionListView = ({
  rules,
  togglingId,
  selection,
  dragHandlers,
  expandedIds,
  onToggleExpanded,
  onEdit,
  onDuplicate,
  onPreviewMatches,
  onShowApplied,
  onDelete,
  onToggleEnabled,
}: AutoTransactionListViewProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Named by position ("rule 2 of 5"), not by the rule's id, which is what
  // dnd-kit would otherwise read out.
  const accessibility = useSortableAccessibility(
    rules.map((r) => r.id),
    "rule",
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (!dragHandlers) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rules.findIndex((r) => r.id === active.id);
    const newIndex = rules.findIndex((r) => r.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = [...rules];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    dragHandlers.onReorder(reordered.map((r) => r.id));
  };

  const body = rules.map((rule, index) => {
    const divider = index < rules.length - 1;
    const rowProps = {
      rule,
      divider,
      togglingId,
      expanded: expandedIds.has(rule.id),
      onToggleExpanded: () => onToggleExpanded(rule.id),
      onEdit,
      onDuplicate,
      onPreviewMatches,
      onShowApplied,
      onDelete,
      onToggleEnabled,
    };
    if (dragHandlers) {
      return (
        <SortableRow key={rule.id} id={rule.id}>
          {(handle) => <Row {...rowProps} leftSlot={handle} showPriorityChip />}
        </SortableRow>
      );
    }
    const leftSlot = selection
      ? selectionCheckbox(rule, selection)
      : priorityAvatar(rule);
    return (
      <Row
        key={rule.id}
        {...rowProps}
        leftSlot={leftSlot}
        showPriorityChip={!!selection}
      />
    );
  });

  const list = (
    <Paper variant="outlined">
      <List sx={{ width: "100%", bgcolor: "background.paper" }}>{body}</List>
    </Paper>
  );

  if (!dragHandlers) return list;
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={accessibility}
    >
      <SortableContext
        items={rules.map((r) => r.id)}
        strategy={verticalListSortingStrategy}
      >
        {list}
      </SortableContext>
    </DndContext>
  );
};
